import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type ReactNode,
} from 'react';
import { getCrossTabCommunication } from '@/services/crossTabCommunication';
import { OUTBOX_OPEN_PARAM } from './externalTapOnLegLink';
import {
  activeSendOf,
  createEmptyOutboxState,
  isFromStorage,
  outboxReducer,
  type OutboxAction,
  type OutboxState,
} from './reducer';
import { loadOutbox, saveOutbox } from './storage';
import type { OutboxSend } from './types';

interface OutboxContextValue {
  /** Every share in the outbox, newest first. */
  sends: readonly OutboxSend[];
  /** Whether the stored outbox has been read yet. */
  isLoaded: boolean;
  isOpen: boolean;
  /** The send the outbox is showing, or null when it is empty. */
  activeSend: OutboxSend | null;
  /**
   * Apply an action. Resolves once the change is stored, with the outbox as it
   * stands after it, so a caller about to hand off to the SMS app can know its
   * state has reached storage first.
   */
  dispatch: (action: OutboxAction) => Promise<OutboxState>;
  /** The send with this id as it stands now, read without waiting on a render. */
  getSend: (sendId: string) => OutboxSend | null;
  /**
   * Open the outbox on a topic's send, or, with no topic, on the newest send
   * with anything left to send.
   */
  openOutbox: (topicId?: string) => void;
  closeOutbox: () => void;
  /** Show another send in the open outbox. */
  showSend: (sendId: string) => void;
  /**
   * Put a share in the outbox, replacing the topic's earlier one. Resolves
   * once it is stored.
   */
  queueSend: (send: OutboxSend) => Promise<void>;
  /** Which sharing interstitials the outbox has shown this app session. */
  interstitialsShown: ReadonlySet<string>;
  recordInterstitialShown: (key: string) => void;
}

const OutboxContext = createContext<OutboxContextValue | undefined>(undefined);

/**
 * The outbox: every share the sender has queued, and where each of its messages
 * stands. It lives above the router, apart from any sharing session, so the
 * sender can come back to sending from anywhere in the app and the drafts'
 * states outlast the flow that queued them. Everything in it is stored.
 *
 * On launch, a send with a message still waiting on "did it send?" means the
 * sender left for Messages and the app was closed behind them, so the outbox
 * opens on it to ask. A launch through the sender's own leg link names the
 * topic to open on in `OUTBOX_OPEN_PARAM` (see `returnThroughOutboxLeg`).
 *
 * Every tab of the app shows the same outbox: a write in one tab tells the
 * others, which read the stored outbox again, the way a verification in one
 * tab updates the rest. So a message confirmed through its own link in a new
 * tab reads as sent in the tab the sender left it waiting in.
 *
 * Its state goes through `outboxReducer`. The provider also keeps the latest
 * state in a ref, reduced as each action is dispatched, so a caller reads the
 * outcome at once and the change is stored before the dispatch resolves. The
 * reducer is pure, so React reducing the same actions in the same order
 * arrives at the same state.
 */
export const OutboxProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, reactDispatch] = useReducer(
    outboxReducer,
    undefined,
    createEmptyOutboxState
  );
  const stateRef = useRef<OutboxState>(state);

  const dispatch = useCallback(
    async (action: OutboxAction): Promise<OutboxState> => {
      const previous = stateRef.current;
      const next = outboxReducer(previous, action);
      stateRef.current = next;
      reactDispatch(action);
      if (next.sends !== previous.sends && !isFromStorage(action)) {
        await saveOutbox(next.sends).catch(error => {
          // eslint-disable-next-line no-console
          console.error('Failed to store the outbox:', error);
        });
      }
      return next;
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    loadOutbox().then(stored => {
      if (cancelled) return;
      dispatch({
        type: 'LOADED',
        stored,
        openOn: new URLSearchParams(window.location.search).get(
          OUTBOX_OPEN_PARAM
        ),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  useEffect(
    () =>
      getCrossTabCommunication().onOutboxChange(() => {
        loadOutbox().then(stored => dispatch({ type: 'RELOADED', stored }));
      }),
    [dispatch]
  );

  const getSend = useCallback(
    (sendId: string) =>
      stateRef.current.sends.find(send => send.id === sendId) ?? null,
    []
  );

  const openOutbox = useCallback(
    (topicId?: string) => {
      dispatch({ type: 'OPEN_OUTBOX', topicId });
    },
    [dispatch]
  );

  const closeOutbox = useCallback(() => {
    dispatch({ type: 'CLOSE_OUTBOX' });
  }, [dispatch]);

  const showSend = useCallback(
    (sendId: string) => {
      dispatch({ type: 'SHOW_SEND', sendId });
    },
    [dispatch]
  );

  const queueSend = useCallback(
    async (send: OutboxSend) => {
      await dispatch({ type: 'QUEUE_SEND', send });
    },
    [dispatch]
  );

  const recordInterstitialShown = useCallback(
    (key: string) => {
      dispatch({ type: 'RECORD_INTERSTITIAL_SHOWN', key });
    },
    [dispatch]
  );

  const value = useMemo<OutboxContextValue>(
    () => ({
      sends: state.sends,
      isLoaded: state.isLoaded,
      isOpen: state.isOpen,
      activeSend: activeSendOf(state),
      dispatch,
      getSend,
      openOutbox,
      closeOutbox,
      showSend,
      queueSend,
      interstitialsShown: state.interstitialsShown,
      recordInterstitialShown,
    }),
    [
      state,
      dispatch,
      getSend,
      openOutbox,
      closeOutbox,
      showSend,
      queueSend,
      recordInterstitialShown,
    ]
  );

  return (
    <OutboxContext.Provider value={value}>{children}</OutboxContext.Provider>
  );
};

export const useOutbox = (): OutboxContextValue => {
  const ctx = useContext(OutboxContext);
  if (!ctx) {
    throw new Error('useOutbox must be used within an OutboxProvider');
  }
  return ctx;
};
